// TraitementOrdreMissionManager.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    Search,
    Filter,
    Check,
    X,
    User,
    Clock,
    AlertCircle,
    FileText,
    Download,
    ChevronDown,
    Briefcase,
    MapPin,
    Loader,
    Eye,
    RotateCcw,
} from "lucide-react";
import {
    getManagerMissionInboxRequest,
    managerMissionDecisionRequest,
    getManagerMissionHistoryRequest,
} from "../../../../redux/actions/missionRequestActions";
import useTreatmentDelegation from "./useTreatmentDelegation";
import useTreatmentAutoRefresh from "./useTreatmentAutoRefresh";
import { confirmTreatmentApproval } from "./confirmTreatmentApproval";
import { exportTreatmentExcel } from "./exportTreatmentExcel";

import "../TraitementStyles/TraitementOrdreMission.css";
import "../TraitementStyles/TreatmentMesOrdres.css";
import { API_BASE_URL } from "../../../../../api";

/* ── SearchableCombobox ── */
const SearchableCombobox = ({ value, onChange, options, placeholder }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState(value || "");
    const wrapperRef = useRef(null);

    useEffect(() => { setQuery(value || ""); }, [value]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredOptions = useMemo(() => {
        if (!query) return options;
        const q = query.toLowerCase();
        return options.filter((o) => o.label.toLowerCase().includes(q));
    }, [options, query]);

    const handleSelect = (opt) => { setQuery(opt.label); onChange(opt.value); setOpen(false); };
    const handleInputChange = (e) => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); };
    const handleClear = () => { setQuery(""); onChange(""); setOpen(false); };

    return (
        <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
            <div style={{ position: "relative" }}>
                <input
                    type="text"
                    className="dcInput"
                    style={{ width: "100%", fontSize: "13px", paddingRight: 56 }}
                    placeholder={placeholder}
                    value={query}
                    onChange={handleInputChange}
                    onFocus={() => setOpen(true)}
                />
                <div style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: 2 }}>
                    {query && (
                        <button type="button" onClick={handleClear} style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 4, lineHeight: 0 }} title={t("Effacer")}>
                            <X size={13} />
                        </button>
                    )}
                    <button type="button" onClick={() => setOpen(o => !o)} style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 4, lineHeight: 0 }} title={t("Afficher la liste")}>
                        <ChevronDown size={14} />
                    </button>
                </div>
            </div>
            {open && filteredOptions.length > 0 && (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.10)", zIndex: 30, maxHeight: 220, overflowY: "auto" }}>
                    {filteredOptions.map((opt) => {
                        const isSelected = opt.value === value;
                        return (
                            <div key={opt.value} onClick={() => handleSelect(opt)}
                                style={{ padding: "8px 12px", fontSize: 13, cursor: "pointer", background: isSelected ? "#eff6ff" : "#fff", color: isSelected ? "#204385" : "#374151", fontWeight: isSelected ? 600 : 400 }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = isSelected ? "#eff6ff" : "#fff")}>
                                {opt.label}
                            </div>
                        );
                    })}
                </div>
            )}
            {open && query && filteredOptions.length === 0 && (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.10)", zIndex: 30, padding: "8px 12px", fontSize: 12, color: "#9ca3af" }}>
                    {t("Aucun résultat")}
                </div>
            )}
        </div>
    );
};

const TraitementMissionsManager = () => {
    const { t, i18n } = useTranslation();
    const locale = i18n.language === "ar" ? "ar-MA" : i18n.language === "en" ? "en-US" : "fr-FR";
    const dispatch = useDispatch();

    const { managerInbox = [], managerHistory = [], loading } = useSelector(
        (state) => state.missionRequests
    );

    const [activeTab, setActiveTab] = useState("enCours");
    const [filterCollab, setFilterCollab] = useState("");
    const [filterType, setFilterType] = useState("");
    const [filterDate, setFilterDate] = useState("");

    const handleResetFilters = () => {
        setFilterCollab("");
        setFilterType("");
        setFilterDate("");
    };

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);
    const [motifRejet, setMotifRejet] = useState("");

    const delegation = useTreatmentDelegation({
        endpoint: "/api/mission-requests/manager/treatment-delegation",
        label: "de missions",
        onUpdated: () => {
            dispatch(getManagerMissionInboxRequest());
            dispatch(getManagerMissionHistoryRequest());
        },
    });

    useEffect(() => {
        dispatch(getManagerMissionInboxRequest());
        dispatch(getManagerMissionHistoryRequest());
    }, [dispatch]);

    useTreatmentAutoRefresh(() => {
        dispatch(getManagerMissionInboxRequest({ silent: true }));
        dispatch(getManagerMissionHistoryRequest({ silent: true }));
    });

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        return new Date(dateStr).toLocaleDateString(locale, {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const getMissionLocation = (demande) =>
        demande?.ordreMissionLieu || demande?.ordreMissionVille || demande?.ordreMissionAdresse || demande?.ordreMissionPays || "";

    const getDownloadUrl = (path) =>
        `${API_BASE_URL}/api/files/download?path=${encodeURIComponent(path || "")}`;

    const getStatutBadge = (statut) => {
        const config = {
            PENDING_MANAGER: { label: "Manager : en attente", bg: "#fff3cd", color: "#856404" },
            PENDING_ADMIN: { label: "Transmis Ã  la RH", bg: "#d1ecf1", color: "#0c5460" },
            APPROVED: { label: "Approuvé", bg: "#d4edda", color: "#155724" },
            REJECTED: { label: "Rejeté", bg: "#f8d7da", color: "#721c24" },
            CANCELLED: { label: "Annulé", bg: "#e2e8f0", color: "#4b5563" },
        };
        const c = config[statut] || config.PENDING_MANAGER;
        return (
            <span className="tomBadge" style={{ backgroundColor: c.bg, color: c.color }}>
                {t(c.label)}
            </span>
        );
    };

    const getRhStatusBadge = (demande) => {
        const rawStatus = String(demande.status || "").toUpperCase();
        if (demande.managerDecision !== true && !["PENDING_ADMIN", "APPROVED", "REJECTED"].includes(rawStatus)) return null;
        const config = {
            PENDING_ADMIN: { label: demande.managerDecision == null ? "Déléguée à la RH" : "RH : en attente", bg: "#d1ecf1", color: "#0c5460" },
            approved: { label: "RH : approuvée", bg: "#d4edda", color: "#155724" },
            rejected: { label: "RH : rejetée", bg: "#f8d7da", color: "#721c24" },
        };
        const key = demande.adminDecision === true ? "approved" : demande.adminDecision === false ? "rejected" : demande.status;
        const c = config[key];
        return c ? <span className="tomBadge" style={{ backgroundColor: c.bg, color: c.color }}>{t(c.label)}</span> : null;
    };

    const handleApprove = (id) => {
        confirmTreatmentApproval(() => dispatch(managerMissionDecisionRequest({ id, decision: true, comment: "" })));
    };

    const handleReject = (demande) => {
        setSelectedDemande(demande);
        setShowRejectModal(true);
    };

    const confirmReject = () => {
        if (!motifRejet.trim() || !selectedDemande) return;
        dispatch(
            managerMissionDecisionRequest({
                id: selectedDemande.id,
                decision: false,
                comment: motifRejet.trim(),
            })
        );
        closeReject();
    };

    const showDetails = (demande) => {
        setSelectedDemande(demande);
        setShowDetailsModal(true);
    };

    const closeDetails = () => setShowDetailsModal(false);

    const closeReject = () => {
        setShowRejectModal(false);
        setMotifRejet("");
        setSelectedDemande(null);
    };

    const mapDemandeToTab = (demande) => {
        if (demande.managerDecision === true) return "approuve";
        if (demande.managerDecision === false) return "rejete";
        if (String(demande.status || "").toUpperCase() === "PENDING_ADMIN") return "approuve";
        if (String(demande.status || "").toUpperCase() === "APPROVED") return "approuve";
        if (String(demande.status || "").toUpperCase() === "REJECTED") return "rejete";
        if (demande.status === "PENDING_MANAGER") return "enCours";
        return null;
    };

    const allDemandes = useMemo(
        () => [...managerInbox, ...managerHistory],
        [managerInbox, managerHistory]
    );

    const collaborateurOptions = useMemo(() => {
        if (!allDemandes) return [];
        const map = new Map();
        allDemandes.forEach((d) => {
            const name = d.employeeName || "";
            if (name && !map.has(name)) {
                map.set(name, { value: name, label: d.employeeMatricule ? `${name} (${d.employeeMatricule})` : name });
            }
        });
        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [allDemandes]);

    const typeOptions = useMemo(() => {
        if (!allDemandes) return [];
        return [...new Set(allDemandes.map((d) => d.ordreMissionLibelle).filter(Boolean))]
            .sort()
            .map((t) => ({ value: t, label: t }));
    }, [allDemandes]);

    const filteredDemandes = useMemo(() => {
        let list = allDemandes.filter((d) => mapDemandeToTab(d) === activeTab);

        if (filterCollab) {
            const val = filterCollab.toLowerCase();
            list = list.filter(
                (d) =>
                    (d.employeeName || "").toLowerCase().includes(val) ||
                    (d.employeeMatricule || "").toLowerCase().includes(val)
            );
        }

        if (filterType) {
            const val = filterType.toLowerCase();
            list = list.filter((d) => (d.ordreMissionLibelle || "").toLowerCase().includes(val));
        }

        if (filterDate) {
            list = list.filter((d) => {
                if (!d.createdAt) return false;
                return new Date(d.createdAt).toISOString().split('T')[0] === filterDate;
            });
        }

        return list;
    }, [allDemandes, activeTab, filterCollab, filterType, filterDate]);

    const tabs = useMemo(() => [
        {
            id: "enCours",
            label: "En cours",
            count: allDemandes.filter((d) => mapDemandeToTab(d) === "enCours").length,
        },
        {
            id: "approuve",
            label: "Approuvées",
            count: allDemandes.filter((d) => mapDemandeToTab(d) === "approuve").length,
        },
        {
            id: "rejete",
            label: "Rejetées",
            count: allDemandes.filter((d) => mapDemandeToTab(d) === "rejete").length,
        },
    ], [allDemandes]);

    const workflows = useMemo(() => {
        return [...new Set(allDemandes.map((d) => d.workflowLevel).filter(Boolean))];
    }, [allDemandes]);

    if (loading && allDemandes.length === 0) {
        return (
            <div className="tomPage">
                <div className="tomWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                    <Loader size={32} className="spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="tomPage">
            <div className="tomWrap">
                {/* Header */}
                <div className="tomHeader" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                    <div>
                        <h1>{t("Traitement des demandes d'ordres de mission")}</h1>
                        <p>{t("Gérez et validez les demandes de mission de vos collaborateurs")}</p>
                    </div>
                    <div className="tavDelegationSetting">
                        <div>
                            <div className="tavDelegationSetting__title">{t("Délégation Admin RH")}</div>
                            <div className="tavDelegationSetting__sub">{t("Transmettre mon traitement des missions à l'admin RH")}</div>
                        </div>
                        <label className="tavSwitch" title={t("Délégation Admin RH")}>
                            <input
                                type="checkbox"
                                checked={delegation.enabled}
                                disabled={delegation.loading || !delegation.loaded}
                                onChange={(e) => delegation.toggle(e.target.checked)}
                            />
                            <span />
                        </label>
                    </div>
                </div>

                {/* Filters Panel */}
                <div className="tomCard" style={{ overflow: "visible" }}>
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: "16px", padding: "4px" }}>
                        <div style={{ minWidth: 220, flex: "1 1 220px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>
                                {t("Collaborateur / Matricule")}
                            </label>
                            <SearchableCombobox
                                value={filterCollab}
                                onChange={setFilterCollab}
                                options={collaborateurOptions}
                                placeholder={t("Nom ou matricule...")}
                            />
                        </div>
                        <div style={{ minWidth: 200, flex: "1 1 200px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>
                                {t("Mission")}
                            </label>
                            <SearchableCombobox
                                value={filterType}
                                onChange={setFilterType}
                                options={typeOptions}
                                placeholder={t("Rechercher une mission...")}
                            />
                        </div>
                        <div style={{ minWidth: 160, flex: "1 1 160px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>
                                {t("Date de la demande")}
                            </label>
                            <input
                                type="date"
                                value={filterDate}
                                onChange={(e) => setFilterDate(e.target.value)}
                                className="dcInput"
                                style={{ width: "100%", fontSize: "13px" }}
                            />
                        </div>
                        <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "flex-end" }}>
                            <button type="button" className="dcBtn dcBtn--outline treatmentExportBtn" style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", height: "38px", background: "#10b981", color: "white", border: "none", whiteSpace: "nowrap" }} onClick={() => exportTreatmentExcel(filteredDemandes, "Ordres_de_mission_manager")}><Download size={14} /> {t("Exporter Excel")}</button>
                            <button
                                type="button"
                                onClick={handleResetFilters}
                                className="dcBtn dcBtn--outline"
                                style={{ padding: "10px 12px", display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", height: "38px", whiteSpace: "nowrap" }}
                            >
                                <RotateCcw size={14} /> {t("Réinitialiser")}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="tomCard tomCard--noPad">
                    <div className="tomTabs">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                className={`tomTab ${activeTab === tab.id ? "is-active" : ""}`}
                            >
                                {t(tab.label)}
                                <span className={`tomCount ${activeTab === tab.id ? "is-active" : ""}`}>
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Table */}
                    <div className="tomTableWrap">
                        {filteredDemandes.length === 0 ? (
                            <div className="tomEmpty">
                                <AlertCircle size={46} />
                                <div>{t("Aucune demande trouvée")}</div>
                            </div>
                        ) : (
                            <table className="tomTable">
                                <thead>
                                    <tr>
                                        <th>{t("Matricule")}</th><th>{t("Collaborateur")}</th><th>{t("Manager direct")}</th><th>{t("Mission")}</th><th>{t("Période")}</th><th>{t("Lieu")}</th><th>{t("Date demande")}</th>
                                        {activeTab === "approuve" && <th>{t("Statut RH")}</th>}
                                        {activeTab === "rejete" && <th>{t("Motif")}</th>}
                                        <th className="is-right">{t("Actions")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredDemandes.map((demande) => (
                                        <tr
                                            key={demande.id}
                                            className={demande.urgent ? "tomRowUrgent" : ""}
                                            onClick={() => showDetails(demande)}
                                            title={demande.urgent ? "Demande urgente" : ""}
                                        >
                                            <td>
                                                <span className="tomPrimary">{demande.employeeMatricule || "N/A"}</span>
                                            </td>
                                            <td>
                                                <div className="tomName">
                                                    <span className="tomPrimary">{demande.employeeName}</span>
                                                    {demande.urgent && <span className="tomFire">🔥</span>}
                                                </div>
                                            </td>
                                            <td><span className="tomSub">{demande.directManagerName || "—"}</span></td>
                                            <td>
                                                <div className="tomType">
                                                    <Briefcase size={16} />
                                                    <div>
                                                        <div className="tomPrimary">{demande.ordreMissionLibelle}</div>
                                                        <div className="tomSub">{demande.ordreMissionCode}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="tomPeriod">
                                                    <div>{formatDate(demande.ordreMissionDateDebut)}</div>
                                                    <div>- {formatDate(demande.ordreMissionDateFin)}</div>
                                                </div>
                                            </td>
                                            <td>
                                                {getMissionLocation(demande) ? (
                                                    <div className="tomLocation">
                                                        <MapPin size={14} />
                                                        {getMissionLocation(demande)}
                                                    </div>
                                                ) : "—"}
                                            </td>
                                            <td>
                                                <span className="tomMuted">{formatDate(demande.createdAt)}</span>
                                            </td>
                                            {activeTab === "approuve" && <td>{getRhStatusBadge(demande) || "-"}</td>}
                                            {activeTab === "rejete" && (
                                                <td>
                                                    <span className="tomMuted" style={{ maxWidth: 160, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {demande.managerComment || demande.adminComment || "—"}
                                                    </span>
                                                </td>
                                            )}
                                            <td className="is-right" onClick={(e) => e.stopPropagation()}>
                                                <div className="tomRowActions">
                                                    {demande.status === "PENDING_MANAGER" && (
                                                        <>
                                                            <button
                                                                type="button"
                                                                className="tomActionBtn tomActionBtn--ok"
                                                                onClick={() => handleApprove(demande.id)}
                                                                title={t("Approuver")}
                                                                disabled={loading}
                                                            >
                                                                <Check size={16} />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="tomActionBtn tomActionBtn--no"
                                                                onClick={() => handleReject(demande)}
                                                                title={t("Refuser")}
                                                                disabled={loading}
                                                            >
                                                                <X size={16} />
                                                            </button>
                                                        </>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="tomIconBtn"
                                                        onClick={() => showDetails(demande)}
                                                        title={t("Voir les détails")}
                                                    >
                                                        <Eye size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* MODAL DÉTAILS */}
            {showDetailsModal && selectedDemande && (
                <div className="tomModal">
                    <div className="tomModal__panel" role="dialog" aria-modal="true">
                        <div className="tomModal__header">
                            <div className="tomModal__headLeft">
                                <div className="tomModal__icon">
                                    <Briefcase size={18} />
                                </div>
                                <div>
                                    <div className="tomModal__title">{t("Demande de mission")} #{selectedDemande.id}</div>
                                    <div className="tomModal__sub">Demandée le {formatDate(selectedDemande.createdAt)}</div>
                                </div>
                            </div>
                            <button type="button" className="tomIconBtn" onClick={closeDetails}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tomModal__body">
                            <div className="tomGrid2">
                                <div className="tomPanel">
                                    <div className="tomPanel__title">
                                        <User size={16} /> Collaborateur
                                    </div>
                                    <div className="tomKv"><span>{t("Matricule")}</span><b>{selectedDemande.employeeMatricule || "—"}</b></div>
                                    <div className="tomKv"><span>{t("Nom")}</span><b>{selectedDemande.employeeName}</b></div>
                                </div>

                                <div className="tomPanel">
                                    <div className="tomPanel__title">
                                        <Briefcase size={16} /> Mission
                                    </div>
                                    <div className="tomKv"><span>{t("Libellé")}</span><b>{selectedDemande.ordreMissionLibelle}</b></div>
                                    <div className="tomKv"><span>{t("Code")}</span><b>{selectedDemande.ordreMissionCode}</b></div>
                                    <div className="tomKv">
                                        <span>{t("Période")}</span>
                                        <b>{formatDate(selectedDemande.ordreMissionDateDebut)} - {formatDate(selectedDemande.ordreMissionDateFin)}</b>
                                    </div>
                                    {getMissionLocation(selectedDemande) && (
                                        <div className="tomKv"><span>{t("Lieu")}</span><b>{getMissionLocation(selectedDemande)}</b></div>
                                    )}
                                    <div className="tomKv">
                                        <span>{t("Workflow")}</span>
                                        <b>
                                            {selectedDemande.workflowLevel === "both" ? "Manager + RH" :
                                                selectedDemande.workflowLevel === "manager" ? "Manager" : "RH"}
                                        </b>
                                    </div>
                                </div>

                                {selectedDemande.justificatifs && selectedDemande.justificatifs.length > 0 && (
                                    <div className="tomPanel tomPanel--full">
                                        <div className="tomPanel__title">
                                            <FileText size={16} /> Justificatifs ({selectedDemande.justificatifs.length})
                                        </div>
                                        <div className="tomDocs">
                                            {selectedDemande.justificatifs.map((j, idx) => (
                                                <a key={idx} className="tomDoc" href={getDownloadUrl(j)} target="_blank" rel="noreferrer">
                                                    <FileText size={18} />
                                                    <span>{j}</span>
                                                    <Download size={16} />
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/*<div className="tomPanel tomPanel--full">*/}
                                {/*    <div className="tomPanel__title">*/}
                                {/*        <FileText size={16} /> Motif de la demande*/}
                                {/*    </div>*/}
                                {/*    <div className="tomText">{selectedDemande.motif || " - "}</div>*/}
                                {/*</div>*/}

                                {selectedDemande.managerDecision !== null && (
                                    <div className="tomPanel tomPanel--full">
                                        <div className="tomPanel__title">
                                            <Check size={16} /> Décision Manager
                                        </div>
                                        <div className="tomKv">
                                            <span>{t("Décision")}</span>
                                            <b style={{ color: selectedDemande.managerDecision ? "#10b981" : "#ef4444" }}>
                                                {selectedDemande.managerDecision ? "Approuvé" : "Rejeté"}
                                            </b>
                                        </div>
                                        {selectedDemande.managerName && (
                                            <div className="tomKv"><span>{t("Par")}</span><b>{selectedDemande.managerName}</b></div>
                                        )}
                                        {selectedDemande.managerDecisionAt && (
                                            <div className="tomKv"><span>{t("Date")}</span><b>{formatDate(selectedDemande.managerDecisionAt)}</b></div>
                                        )}
                                        {selectedDemande.managerComment && (
                                            <div className="tomRejectBox">
                                                <div className="tomRejectLabel">{t("Commentaire")}</div>
                                                <div className="tomRejectText">{selectedDemande.managerComment}</div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="tomModal__footer">
                            {selectedDemande.status === "PENDING_MANAGER" ? (
                                <>
                                    <button
                                        className="tomBtn tomBtn--danger"
                                        onClick={() => { closeDetails(); handleReject(selectedDemande); }}
                                        disabled={loading}
                                    >
                                        {t("Refuser")}
                                    </button>
                                    <button
                                        className="tomBtn tomBtn--ok"
                                        onClick={() => { handleApprove(selectedDemande.id); closeDetails(); }}
                                        disabled={loading}
                                    >
                                        {loading ? <Loader size={16} className="spin" /> : t("Approuver")}
                                    </button>
                                    <button className="tomBtn tomBtn--ghost" onClick={closeDetails}>
                                        Fermer
                                    </button>
                                </>
                            ) : (
                                <button className="tomBtn tomBtn--ghost" onClick={closeDetails}>
                                    Fermer
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL REJET */}
            {showRejectModal && selectedDemande && (
                <div className="tomModal">
                    <div className="tomModal__panel tomModal__panel--small" role="dialog" aria-modal="true">
                        <div className="tomModal__header">
                            <div className="tomModal__headLeft">
                                <div className="tomModal__icon tomModal__icon--danger">
                                    <AlertCircle size={18} />
                                </div>
                                <div>
                                    <div className="tomModal__title">{t("Refuser la demande de mission")}</div>
                                    <div className="tomModal__sub">
                                        {selectedDemande.employeeName} â€” {selectedDemande.ordreMissionLibelle}
                                    </div>
                                </div>
                            </div>
                            <button type="button" className="tomIconBtn" onClick={closeReject}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tomModal__body">
                            <div className="tomMiniCard">
                                <div className="tomMiniRow">
                                    <span className="tomMuted">{t("Mission")}</span>
                                    <b>{selectedDemande.ordreMissionCode} â€“ {selectedDemande.ordreMissionLibelle}</b>
                                </div>
                                <div className="tomMiniRow">
                                    <span className="tomMuted">{t("Période")}</span>
                                    <b>{formatDate(selectedDemande.ordreMissionDateDebut)} - {formatDate(selectedDemande.ordreMissionDateFin)}</b>
                                </div>
                            </div>

                            <label className="tomLabel">
                                Motif du refus <span className="tomReq">*</span>
                            </label>
                            <textarea
                                value={motifRejet}
                                required
                                onChange={(e) => setMotifRejet(e.target.value)}
                                rows={5}
                                placeholder={t("Expliquez pourquoi cette demande est refusée...")}
                                className="tomTextarea"
                            />
                            <div className="tomHelp">{t("Ce motif sera visible par le collaborateur")}</div>
                        </div>

                        <div className="tomModal__footer">
                            <button type="button" className="tomBtn tomBtn--ghost" onClick={closeReject}>
                                {t("Annuler")}
                            </button>
                            <button
                                type="button"
                                className="tomBtn tomBtn--danger"
                                onClick={confirmReject}
                                disabled={!motifRejet.trim() || loading}
                            >
                                {loading ? <Loader size={16} className="spin" /> : "Confirmer le refus"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TraitementMissionsManager;
